import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { ArrowLeftOutline, Button, DocumentDownloadOutline, Flex, HStack, Text, TrashOutline, useToast } from '@bonusly/design-system';

import { useMigration } from '../../hooks/use-migration';
import { usePreviewPreference } from '../../hooks/use-preview-preference';

interface PreviewBannerProps {
	variant: 'opt-in' | 'opt-out';
}

export const PreviewBanner: FC<PreviewBannerProps> = ({ variant }) => {
	const navigate = useNavigate();
	const { enablePreview, disablePreview } = usePreviewPreference();
	const { migrate, clear, isMigrating, isClearing } = useMigration();
	const toast = useToast();

	const handleEnablePreview = () => {
		enablePreview();
		navigate('/meet');
	};

	const handleDisablePreview = () => {
		disablePreview();
		navigate('/growth/meet');
	};

	const handleMigrate = async () => {
		try {
			const result = await migrate.mutateAsync();
			toast({
				title: result.migrated_count > 0 ? `Migrated ${result.migrated_count} meeting${result.migrated_count === 1 ? '' : 's'}` : 'No meetings to migrate',
				status: 'success',
				duration: 3000,
			});
		} catch {
			toast({
				title: 'Failed to migrate meetings',
				status: 'error',
				duration: 5000,
			});
		}
	};

	const handleClear = async () => {
		try {
			const result = await clear.mutateAsync();
			toast({
				title:
					result.cleared_count > 0
						? `Cleared ${result.cleared_count} migrated meeting${result.cleared_count === 1 ? '' : 's'}`
						: 'No migrated meetings to clear',
				status: 'success',
				duration: 3000,
			});
		} catch {
			toast({
				title: 'Failed to clear migrated meetings',
				status: 'error',
				duration: 5000,
			});
		}
	};

	// Button styles for buttons on hashtag background - works in light/dark modes
	const bannerButtonStyles = {
		bg: 'bg.surface',
		color: 'hashtag.fg',
		borderColor: 'hashtag.fg',
		borderWidth: '1px',
		_hover: {
			bg: 'hashtag.bgAlphaHover',
		},
		_active: {
			bg: 'hashtag.bgAlphaActive',
		},
	};

	if (variant === 'opt-in') {
		return (
			<Flex align="center" gap={3} px={4} py={2} bg="hashtag.bgAlpha" borderRadius="lg" mb={4}>
				<Text fontSize="sm" color="hashtag.fgOnSurface" flex={1}>
					A new 1:1 experience is available — view all your 1:1s in one place
				</Text>
				<Button size="sm" onClick={handleEnablePreview} {...bannerButtonStyles}>
					Try it out
				</Button>
			</Flex>
		);
	}

	// opt-out variant - single line with emoji, message, icon buttons
	return (
		<Flex align="center" gap={3} px={4} py={2} bg="hashtag.bgAlpha" borderRadius="lg" mb={4}>
			<Text fontSize="sm" color="hashtag.fgOnSurface" flex={1}>
				You're previewing the new 1:1s — bring your old meetings here or go back anytime
			</Text>
			<HStack spacing={2}>
				<Button size="sm" leftIcon={<ArrowLeftOutline size="sm" />} onClick={handleDisablePreview} {...bannerButtonStyles}>
					Back
				</Button>
				<Button size="sm" leftIcon={<DocumentDownloadOutline size="sm" />} onClick={handleMigrate} isLoading={isMigrating} {...bannerButtonStyles}>
					Migrate
				</Button>
				<Button size="sm" leftIcon={<TrashOutline size="sm" />} onClick={handleClear} isLoading={isClearing} {...bannerButtonStyles}>
					Clear
				</Button>
			</HStack>
		</Flex>
	);
};

PreviewBanner.displayName = 'PreviewBanner';
